import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ClapperboardIcon, Files, Users, ClipboardList, GanttChartSquare, MessageSquare, Sparkles, Receipt } from "lucide-react";
import { shotsApi, filesApi, projectMembersApi, taskCommentsApi, expensesApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = [
  { key: "plan", label: "Plan", icon: GanttChartSquare, to: "plan" },
  { key: "shots", label: "Shots", icon: ClapperboardIcon, to: "shots" },
  { key: "files", label: "Files", icon: Files, to: "files" },
  { key: "members", label: "Members", icon: Users, to: "members" },
  { key: "call-sheet", label: "Call Sheet", icon: ClipboardList, to: "call-sheet" },
  { key: "expenses", label: "Expenses", icon: Receipt, to: "expenses", adminOnly: true },
  { key: "discussion", label: "Discussion", icon: MessageSquare, to: "discussion" },
  { key: "updates", label: "Updates", icon: Sparkles, to: "updates" },
];

export default function ProjectSubNav({ projectId, projectMyRole }) {
  const { user } = useAuth();
  // Admin-only tabs (Expenses) are visible to either the global admin
  // OR the per-project admin for this specific project. ProjectShell
  // passes `projectMyRole` down so we don't have to duplicate the fetch.
  const isAdmin =
    user?.role === "admin" ||
    !!user?.is_admin ||
    projectMyRole === "admin";
  const [counts, setCounts] = useState({});
  const [pendingReimburseCount, setPendingReimburseCount] = useState(0);

  // Cross-project pending-reimbursement count powers the notification dot
  // on the Expenses tab. Fetched once on mount; refresh happens naturally
  // on route change since ProjectSubNav re-mounts per project.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await expensesApi.pendingSummary();
        if (!cancelled) setPendingReimburseCount(s.pending_count || 0);
      } catch {
        /* silent — the tab still works, we just don't dot it */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, projectId]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      const [shots, files, members, comments] = await Promise.allSettled([
        shotsApi.list(projectId),
        filesApi.list(projectId),
        projectMembersApi.list(projectId),
        taskCommentsApi.counts(projectId),
      ]);
      if (cancelled) return;
      const commentTotal =
        comments.status === "fulfilled"
          ? Object.values(comments.value || {}).reduce((a, b) => a + b, 0)
          : null;
      setCounts({
        shots: shots.status === "fulfilled" ? shots.value.length : null,
        files: files.status === "fulfilled" ? files.value.length : null,
        members: members.status === "fulfilled" ? members.value.length : null,
        discussion: commentTotal,
      });
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  return (
    <nav
      data-testid="project-subnav"
      className="border-b border-zinc-800/70 bg-zinc-950/40"
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center gap-1">
        {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => {
          const Icon = t.icon;
          const c = counts[t.key];
          const showDot = t.key === "expenses" && pendingReimburseCount > 0;
          return (
            <NavLink
              key={t.key}
              to={`/projects/${projectId}/${t.to}`}
              end={t.to === "plan"}
              data-testid={`tab-${t.key}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                  isActive
                    ? "border-amber-500 text-zinc-100"
                    : "border-transparent text-zinc-500 hover:text-zinc-200 hover:border-zinc-700"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {typeof c === "number" && c > 0 && (
                <span className="text-[10px] font-mono text-zinc-500">· {c}</span>
              )}
              {showDot && (
                <span
                  data-testid="expenses-nav-dot"
                  title={`${pendingReimburseCount} pending reimbursement${pendingReimburseCount === 1 ? "" : "s"}`}
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-zinc-950 animate-pulse"
                />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
